import { PageEnum } from 'store/remote/types';
import { ISODateString } from './types';
import {
  EngageFlatIsHMO,
  EngageFlatIsCommerciallyOwned,
  EngagePropertiesSortField,
  EngagePropertiesSortDirection,
  FlatIssueType,
  CommunicationStatusFlags,
  CommunicationResponseStatus,
  FlatRequiresResidentSupport,
} from 'types/DTOs/BlockManagerDTOs';
import { ICONS } from 'components/Icon';

export enum CommunicationType {
  email = 'email',
  letter = 'letter',
}

export enum CommunicationStatus {
  requested = 'requested',
  sent = 'sent',
  failed = 'failed',
  delivered = 'delivered',
}

export enum DataSource {
  'engage' = 'engage',
  'app' = 'app',
}

export const ContactAddedVia: Record<DataSource, { name: string }> = {
  [DataSource.engage]: {
    name: 'Engage',
  },
  [DataSource.app]: {
    name: 'App',
  },
};

export enum ResidentSurveyStatus {
  'pending' = 'pending',
  'ready' = 'ready',
  'in-progress' = 'in-progress',
  'complete' = 'complete',
  'expired' = 'expired',
  'archived' = 'archived',
}

export const ResidentSurveyStatusData: Record<
  ResidentSurveyStatus,
  { key: ResidentSurveyStatus; name: string }
> = {
  [ResidentSurveyStatus.pending]: {
    key: ResidentSurveyStatus.pending,
    name: 'Incomplete',
  },
  [ResidentSurveyStatus.ready]: {
    key: ResidentSurveyStatus.ready,
    name: 'Incomplete',
  },
  [ResidentSurveyStatus['in-progress']]: {
    key: ResidentSurveyStatus['in-progress'],
    name: 'Incomplete',
  },
  [ResidentSurveyStatus.complete]: {
    key: ResidentSurveyStatus.complete,
    name: 'Complete',
  },
  [ResidentSurveyStatus.expired]: {
    key: ResidentSurveyStatus.expired,
    name: 'Expired',
  },
  [ResidentSurveyStatus.archived]: {
    key: ResidentSurveyStatus.archived,
    name: 'Archived',
  },
};

export enum FlatOccupationStatus {
  OCCUPIED = 'occupied',
  UNOCCUPIED = 'unoccupied',
  UNKNOWN = 'unknown',
}

export const EngageFlatsStatusFilters = [
  {
    id: 'complete',
    name: 'Complete',
  },
  {
    id: 'incomplete',
    name: 'Incomplete',
  },
] as const;

export const EngageFlatsOccupationStatusFilters = [
  {
    id: FlatOccupationStatus.OCCUPIED,
    name: 'Occupied',
  },
  {
    id: FlatOccupationStatus.UNOCCUPIED,
    name: 'Unoccupied',
  },
  {
    id: FlatOccupationStatus.UNKNOWN,
    name: 'Not Known',
  },
] as const;

export const EngageFlatsIssueTypeFilters = [
  {
    id: FlatIssueType.RESIDENT_ISSUES,
    name: 'Resident Issues',
  },
  {
    id: FlatIssueType.OWNER_ISSUES,
    name: 'Owner Issues',
  },
  {
    id: FlatIssueType.EMAIL_ISSUES,
    name: 'Email Issues',
  },
];

export const EngageFlatsRequiresResidentSupportFilters = [
  {
    id: FlatRequiresResidentSupport.REQUIRED,
    name: 'Required',
  },
  {
    id: FlatRequiresResidentSupport.NOT_REQUIRED,
    name: 'Not Required',
  },
];

export type EngageFlatsFilter = {
  search: string;
  occupationStatuses: FlatOccupationStatus[];
  issueContactTypes: FlatIssueType[];
  requiresResidentSupport: FlatRequiresResidentSupport[];
};

export const EngageFlatsDefaultFilter: EngageFlatsFilter = {
  search: '',
  occupationStatuses: EngageFlatsOccupationStatusFilters.map(
    (status) => status.id,
  ),
  issueContactTypes: [], // set to empty to ensure filters do not apply
  requiresResidentSupport: [], // set to empty to ensure filters do not apply
};

export type EngagePropertiesFilter = {
  search: string;
  sortBy: EngagePropertiesSortField | null;
  sortDirection: EngagePropertiesSortDirection | null;
};

export const EngagePropertiesDefaultFilter: EngagePropertiesFilter = {
  search: '',
  sortBy: null,
  sortDirection: null,
};

export enum FlatContactType {
  resident = 'resident',
  residentOwner = 'resident-owner',
  owner = 'owner',
  movedSold = 'moved-sold',
  noAssociation = 'no-association',
  other = 'other',
  lettingAgent = 'letting-agent',
  onBehalfOf = 'on-behalf-of',
}

export const FlatContactTypes: Record<
  FlatContactType,
  {
    id: FlatContactType;
    name: string;
    namePlural: string;
    ordering: number;
  }
> = {
  [FlatContactType.owner]: {
    id: FlatContactType.owner,
    name: 'Owner',
    namePlural: 'Owners',
    ordering: 1,
  },
  [FlatContactType.residentOwner]: {
    id: FlatContactType.residentOwner,
    name: 'Owner & Resident',
    namePlural: 'Owners & Residents',
    ordering: 2,
  },
  [FlatContactType.resident]: {
    id: FlatContactType.resident,
    name: 'Resident',
    namePlural: 'Residents',
    ordering: 3,
  },
  [FlatContactType.lettingAgent]: {
    id: FlatContactType.lettingAgent,
    name: 'Letting Agent',
    namePlural: 'Letting Agents',
    ordering: 4,
  },
  [FlatContactType.onBehalfOf]: {
    id: FlatContactType.onBehalfOf,
    name: 'On Behalf Of',
    namePlural: 'On Behalf Of',
    ordering: 5,
  },
  [FlatContactType.other]: {
    id: FlatContactType.other,
    name: 'Other',
    namePlural: 'Others',
    ordering: 6,
  },
  [FlatContactType.noAssociation]: {
    id: FlatContactType.noAssociation,
    name: 'Other',
    namePlural: 'Others',
    ordering: 99,
  },
  [FlatContactType.movedSold]: {
    id: FlatContactType.movedSold,
    name: '',
    namePlural: '',
    ordering: 99,
  },
};

export enum EngageContactArchivedReason {
  notMe = 'not-me',
  movedOut = 'moved-out',
  stoppedOwning = 'stopped-owning',
  movedOutStoppedOwning = 'moved-out-stopped-owning',
  mistake = 'mistake',
  other = 'other',
}

export type EngageFlatContact = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  isPrimary: boolean;
  isOwner: boolean;
  isResident: boolean;
  isUnder16: boolean;
  contactType: FlatContactType;
  hasSupportNeeds: boolean;
  hasCognitiveImpairment: boolean;
  hasHearingImpairment: boolean;
  hasMobilityLimitation: boolean;
  hasSocialAnxiety: boolean;
  hasEmergencyEvacuationStatement: boolean;
  hasGivenSupportNeedsConsent: boolean;
  comments: string;
  internalNotes: string;
  addedTimestamp: ISODateString;
  addedVia: DataSource;
  archivedTimestamp: ISODateString | null;
  archivedVia: DataSource | null;
  archivedReason: EngageContactArchivedReason | null;
  confirmedDate: ISODateString | null;
  confirmationExpiryDate: ISODateString | null;
};

export type EngagePropertyListItem = {
  id: string;
  reference: string;
  customReference: string;
  name: string;
  parentName: string;
  addressLines: string[];
  postcode: string;
  organisationName: string;
  noOfFlats: number;
  noOfContacts: number;
};

export type EngageFlatIssues = {
  hasNoResidents: boolean | null;
  hasNoOwners: boolean | null;
  isMissingResidents: boolean | null;
  hasExpiredResidentConfirmations: boolean | null;
  hasExpiredOwnerConfirmations: boolean | null;
  hasOwnerIssues: boolean;
  hasResidentIssues: boolean;
  hasEmailIssues: boolean;
};

export type EngageFlatConfirmations = {
  oldestResidentConfirmationDate: ISODateString | null;
  oldestOwnerConfirmationDate: ISODateString | null;
  residentConfirmationExpiryDate: ISODateString | null;
  ownerConfirmationExpiryDate: ISODateString | null;
};

export type EngageFlatListItem = {
  id: string;
  reference: string;
  customReference: string;
  name: string;
  occupationStatus: FlatOccupationStatus;
  contacts: {
    id: string;
    name: string;
    email: string;
    isOwner: boolean;
    isResident: boolean;
    isPrimary: boolean;
    isUnder16: boolean;
    hasSupportNeeds: boolean;
    contactType: FlatContactType;
    confirmedDate: ISODateString | null;
    confirmationExpiryDate: ISODateString | null;
  }[];
  isHMO: EngageFlatIsHMO;
  isCommerciallyOwned: EngageFlatIsCommerciallyOwned;
  isMissingResidents: boolean | null;
  issues: EngageFlatIssues;
  confirmations: EngageFlatConfirmations;
  matchingContactIds: string[];
};

export enum CommunicationAccessedFrom {
  'letterQr' = 'letter-qr',
  'emailLink' = 'email-link',
  'app' = 'app',
  'shortCode' = 'short-code',
  'unknown' = 'unknown',
}

export type EngageCommunicationListItem = {
  id: string;
  createdDate: ISODateString;
  name: string;
  type: CommunicationType;
  contactId: string | null;
  address: string;
  status: CommunicationStatus;
  statusDate: ISODateString;
  link: string;
  linkAccessed: {
    from: CommunicationAccessedFrom;
    date: ISODateString;
  } | null;
  linkStatus: CommunicationResponseStatus;
  statusFlags: CommunicationStatusFlags;
  previewContentURL: string | null;
};

export type EngageStore = {
  type: PageEnum.engage;
  data: {
    isWaitingInitLoad: boolean;
    isLoading: boolean;
    properties: {
      totalCount: number;
      pageNumber: number;
      dataOrder: string[];
      data: Record<string, EngagePropertyListItem>;
      filters: EngagePropertiesFilter;
    };
    flats: {
      totalCount: number;
      pageNumber: number;
      dataOrder: string[];
      data: Record<string, EngageFlatListItem>;
      filters: EngageFlatsFilter;
    };
    flat: {
      property: {
        id: string;
        reference: string;
        customReference: string;
        name: string;
        parentName: string;
        addressLines: string[];
        postcode: string;
      };
      flat: Omit<EngageFlatListItem, 'contacts' | 'residentSurveyStatus'>;
      contacts: EngageFlatContact[];
      archivedContacts: null | EngageFlatContact[];
      communications: EngageCommunicationListItem[];
    } | null;
    bulkCommunication: {
      propertyId: string;
      flatIds: string[];
      contactsByFlat: Record<string, EngageFlatContact[]>;
      loading: boolean;
    } | null;
  };
};

export const OccupationStatusIcons: Record<FlatOccupationStatus, ICONS> = {
  [FlatOccupationStatus.OCCUPIED]: ICONS.HouseOccupationOccupied,
  [FlatOccupationStatus.UNOCCUPIED]: ICONS.HouseOccupationUnoccupied,
  [FlatOccupationStatus.UNKNOWN]: ICONS.HouseOccupationUnknown,
};
